import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { GuiaProduccionState, GuiaProduccionFormData, ArticuloProduccion, EstadoGuiaProduccion, GuiaProduccionDetailResponse } from '../types';
import { GuiaProduccionService } from '../services/GuiaProduccionService';
import { errorService } from '../../../../services/errorService';

interface GuiaProduccionActions {
  fetchGuias: (page?: number, pageSize?: number) => Promise<void>;
  fetchGuiaById: (id: number) => Promise<GuiaProduccionDetailResponse | null>;
  saveGuia: (formData: GuiaProduccionFormData, productos: ArticuloProduccion[]) => Promise<number | null>;
  updateEstado: (id: number, estado: EstadoGuiaProduccion) => Promise<boolean>;
  deleteGuia: (id: number) => Promise<boolean>;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  clearError: () => void;
  reset: () => void;
}

type GuiaProduccionStore = GuiaProduccionState & GuiaProduccionActions;

const initialState: GuiaProduccionState = {
  guias: [],
  loading: false,
  error: null,
  totalCount: 0,
  currentPage: 1,
  pageSize: 10,
};

export const useGuiaProduccionStore = create<GuiaProduccionStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      fetchGuias: async (page, pageSize) => {
        const { currentPage, pageSize: currentPageSize } = get();
        const targetPage = page ?? currentPage;
        const targetPageSize = pageSize ?? currentPageSize;

        set({ loading: true, error: null });

        try {
          const response = await GuiaProduccionService.getAll(targetPage, targetPageSize);
          set({
            guias: response.data,
            totalCount: response.totalCount,
            currentPage: targetPage,
            pageSize: targetPageSize,
            loading: false,
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Error al cargar las guías',
            loading: false,
          });
        }
      },

      fetchGuiaById: async (id: number) => {
        set({ loading: true, error: null });

        try {
          const guia = await GuiaProduccionService.getById(id);
          set({ loading: false });
          return guia;
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Error al cargar la guía',
            loading: false,
          });
          return null;
        }
      },

      saveGuia: async (formData: GuiaProduccionFormData, productos: ArticuloProduccion[]) => {
        set({ loading: true, error: null });

        try {
          const id = await GuiaProduccionService.save(formData, productos);
          const { fetchGuias } = get();
          await fetchGuias();
          set({ loading: false });
          errorService.showSuccess('Guía de producción guardada exitosamente');
          return id;
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Error al guardar la guía',
            loading: false,
          });
          return null;
        }
      },

      updateEstado: async (id: number, estado: EstadoGuiaProduccion) => {
        set({ loading: true, error: null });

        try {
          await GuiaProduccionService.updateEstado(id, estado);
          const { fetchGuias } = get();
          await fetchGuias();
          set({ loading: false });
          return true;
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Error al actualizar el estado',
            loading: false,
          });
          return false;
        }
      },

      deleteGuia: async (id: number) => {
        set({ loading: true, error: null });

        try {
          await GuiaProduccionService.delete(id);
          const { fetchGuias } = get();
          await fetchGuias();
          set({ loading: false });
          return true;
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Error al eliminar la guía',
            loading: false,
          });
          return false;
        }
      },

      setPage: (page: number) => {
        set({ currentPage: page });
      },

      setPageSize: (pageSize: number) => {
        set({ pageSize, currentPage: 1 });
      },

      clearError: () => {
        set({ error: null });
      },

      reset: () => {
        set(initialState);
      },
    }),
    { name: 'guia-produccion-store' }
  )
);